import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { cmsService } from '@services/api'
import { 
  Calendar, 
  Plus, 
  Search, 
  // Filter, 
  Download,
  Eye,
  Edit,
  Trash2,
  Clock,
  MapPin,
  Navigation,
  CheckCircle,
  XCircle,
  // AlertCircle,
  // Route,
  Truck,
  Settings
} from 'lucide-react'
import {
  VStack,
  HStack,
  Text,
  Heading,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Card,
  CardBody,
  CardHeader,
  Center,
  Flex,
  // useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  SimpleGrid,
  Box,
  Avatar,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Select,
  useToast,
  Tooltip,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Switch,
} from '@chakra-ui/react'

// Types for timetable data
interface Timetable {
  id: string;
  daladalaId: string;
  routeId: string;
  dayOfWeek: number;
  departureTime: string;
  arrivalTime: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  daladala?: {
    id: string;
    registrationNumber: string;
    driver?: {
      id: string;
      firstName: string;
      lastName: string;
      phoneNumber?: string;
    };
  };
  route?: {
    id: string;
    name: string;
    startPoint: string;
    endPoint: string;
  };
  trips?: Array<{
    id: string;
    status: string;
    scheduledDeparture: string;
  }>;
}

interface TimetableFormData {
  daladalaId: string
  routeId: string
  daySelection: 'single' | 'range' | 'all'
  dayOfWeek: number
  rangeStart: number
  rangeEnd: number
  departureTime: string
  arrivalTime: string
}

const dayNames = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
]

const formatTime = (value?: string) => {
  if (!value) return 'N/A'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'N/A'
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Nairobi' })
}

const formatDuration = (start?: string, end?: string) => {
  if (!start || !end) return 'N/A'
  const startDate = new Date(start)
  const endDate = new Date(end)
  const diff = endDate.getTime() - startDate.getTime()
  if (!Number.isFinite(diff) || diff <= 0) return 'N/A'
  const hours = Math.floor(diff / (1000 * 60 * 60))
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
  const parts: string[] = []
  if (hours > 0) parts.push(`${hours}h`)
  parts.push(`${minutes.toString().padStart(2, '0')}m`)
  return parts.join(' ')
}

const getErrorMessage = (error: any, fallback: string) => {
  if (!error) return fallback
  const apiError = error?.response?.data?.error
  if (!apiError) return error?.message || fallback
  if (typeof apiError === 'string') return apiError
  if (typeof apiError?.message === 'string') return apiError.message
  try {
    return JSON.stringify(apiError)
  } catch {
    return fallback
  }
}

function TimetableFormModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [formData, setFormData] = useState<TimetableFormData>({
    daladalaId: '',
    routeId: '',
    daySelection: 'single',
    dayOfWeek: 1,
    rangeStart: 1,
    rangeEnd: 5,
    departureTime: '',
    arrivalTime: ''
  })
  const [isCreatingMultiple, setIsCreatingMultiple] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const toast = useToast()
  const queryClient = useQueryClient()

  // Fetch available daladalas and routes for the form
  const { data: daladalasData } = useQuery('vehicles', () => cmsService.getVehicles())
  const { data: routesData } = useQuery('routes', () => cmsService.getRoutes())

  const createTimetableMutation = useMutation(
    ({ daladalaId, timetableData }: { daladalaId: string; timetableData: any }) =>
      cmsService.createTimetable(daladalaId, timetableData)
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.daladalaId || !formData.routeId || !formData.departureTime || !formData.arrivalTime) {
      toast({
        title: 'Missing Information',
        description: 'Please select a daladala, route, departure time, and arrival time.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      })
      return
    }

    setIsLoading(true)

    const selectedDays = (() => {
      if (formData.daySelection === 'all') {
        return Array.from({ length: 7 }, (_, idx) => idx)
      }
      if (formData.daySelection === 'range') {
        if (formData.rangeStart > formData.rangeEnd) {
          toast({
            title: 'Invalid Range',
            description: 'Start day must be before or equal to end day.',
            status: 'error',
            duration: 4000,
            isClosable: true,
          })
          return null
        }
        return Array.from(
          { length: formData.rangeEnd - formData.rangeStart + 1 },
          (_, idx) => formData.rangeStart + idx
        )
      }
      return [formData.dayOfWeek]
    })()

    if (!selectedDays) {
      setIsLoading(false)
      return
    }

    setIsCreatingMultiple(selectedDays.length > 1)

    const buildPayload = (day: number) => ({
      daladalaId: formData.daladalaId,
      timetableData: {
        routeId: formData.routeId,
        dayOfWeek: day,
        departureTime: formData.departureTime,
        arrivalTime: formData.arrivalTime
      }
    })

    try {
      if (selectedDays.length > 1) {
        let successCount = 0
        const failures: string[] = []

        for (const day of selectedDays) {
          try {
            await createTimetableMutation.mutateAsync(buildPayload(day))
            successCount += 1
          } catch (error: any) {
            failures.push(getErrorMessage(error, 'Failed to create timetable'))
          }
        }

        await queryClient.invalidateQueries('timetables')
        await queryClient.invalidateQueries('dashboard-stats')

        if (failures.length === 0) {
          toast({
            title: 'Timetables Created',
            description: 'Successfully created timetables for all 7 days of the week.',
            status: 'success',
            duration: 5000,
            isClosable: true,
          })
        } else if (successCount > 0) {
          toast({
            title: 'Partial Success',
            description: `Created ${successCount} timetables. ${failures.length} failed.`,
            status: 'warning',
            duration: 6000,
            isClosable: true,
          })
        } else {
          throw new Error(failures[0] || 'Failed to create timetables')
        }
      } else {
        await createTimetableMutation.mutateAsync(
          buildPayload(selectedDays[0])
        )
        await queryClient.invalidateQueries('timetables')
        await queryClient.invalidateQueries('dashboard-stats')
        toast({
          title: 'Timetable Created',
          description: 'Timetable has been successfully created.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
      }

      setFormData({
        daladalaId: '',
        routeId: '',
        daySelection: 'single',
        dayOfWeek: 1,
        rangeStart: 1,
        rangeEnd: 5,
        departureTime: '',
        arrivalTime: ''
      })
      onClose()
    } catch (error: any) {
      toast({
        title: 'Error',
        description: getErrorMessage(error, 'Failed to create timetable(s). Please try again.'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setIsLoading(false)
      setIsCreatingMultiple(false)
    }
  }

  const daladalas = daladalasData?.data || []
  const routes = routesData?.data || []

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create New Timetable</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <form onSubmit={handleSubmit}>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Daladala Vehicle</FormLabel>
                <Select
                  value={formData.daladalaId}
                  onChange={(e) => setFormData({ ...formData, daladalaId: e.target.value })}
                  placeholder="Select a daladala"
                >
                  {daladalas.map((daladala: any) => (
                    <option key={daladala.id} value={daladala.id}>
                      {daladala.registrationNumber}
                      {daladala.driver ? ` - ${[daladala.driver.firstName, daladala.driver.lastName].filter(Boolean).join(' ').trim() || 'Driver'}` : ''}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Route</FormLabel>
                <Select
                  value={formData.routeId}
                  onChange={(e) => setFormData({ ...formData, routeId: e.target.value })}
                  placeholder="Select a route"
                >
                  {routes.map((route: any) => (
                    <option key={route.id} value={route.id}>
                      {route.name} ({route.startPoint} → {route.endPoint})
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Apply Schedule To</FormLabel>
                <Select
                  value={formData.daySelection}
                  onChange={(e) => setFormData({
                    ...formData,
                    daySelection: e.target.value as 'single' | 'range' | 'all'
                  })}
                >
                  <option value="single">Single Day</option>
                  <option value="range">Range of Days</option>
                  <option value="all">All Days of the Week</option>
                </Select>
              </FormControl>

              {formData.daySelection === 'single' && (
                <FormControl isRequired>
                  <FormLabel>Day of Week</FormLabel>
                  <Select
                    value={formData.dayOfWeek}
                    onChange={(e) => setFormData({
                      ...formData,
                      dayOfWeek: parseInt(e.target.value)
                    })}
                  >
                    {dayNames.map((day, index) => (
                      <option key={index} value={index}>
                        {day}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              )}

              {formData.daySelection === 'range' && (
                <HStack spacing={4} w="full">
                  <FormControl isRequired>
                    <FormLabel>Start Day</FormLabel>
                    <Select
                      value={formData.rangeStart}
                      onChange={(e) => setFormData({
                        ...formData,
                        rangeStart: parseInt(e.target.value)
                      })}
                    >
                      {dayNames.map((day, index) => (
                        <option key={index} value={index}>
                          {day}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>End Day</FormLabel>
                    <Select
                      value={formData.rangeEnd}
                      onChange={(e) => setFormData({
                        ...formData,
                        rangeEnd: parseInt(e.target.value)
                      })}
                    >
                      {dayNames.map((day, index) => (
                        <option key={index} value={index}>
                          {day}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </HStack>
              )}

              {/* Information boxes */}
              {formData.daySelection === 'range' && (
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <AlertTitle fontSize="sm">Creating Multiple Timetables</AlertTitle>
                    <AlertDescription fontSize="sm">
                      Timetables will be created for each day between {dayNames[formData.rangeStart]} and {dayNames[formData.rangeEnd]} (inclusive).
                    </AlertDescription>
                  </Box>
                </Alert>
              )}

              {formData.daySelection === 'all' && (
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <AlertTitle fontSize="sm">Creating Multiple Timetables</AlertTitle>
                    <AlertDescription fontSize="sm">
                      This will create 7 separate timetable entries (one for each day of the week: Sunday through Saturday) with the same departure and arrival times.
                    </AlertDescription>
                  </Box>
                </Alert>
              )}

              <HStack spacing={4} w="full">
                <FormControl isRequired>
                  <FormLabel>Departure Time</FormLabel>
                  <Input
                    type="time"
                    value={formData.departureTime}
                    onChange={(e) => setFormData({ ...formData, departureTime: e.target.value })}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Arrival Time</FormLabel>
                  <Input
                    type="time"
                    value={formData.arrivalTime}
                    onChange={(e) => setFormData({ ...formData, arrivalTime: e.target.value })}
                  />
                </FormControl>
              </HStack>

              <HStack spacing={4} w="full" pt={4}>
                <Button variant="outline" onClick={onClose} flex={1}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  colorScheme="brand" 
                  isLoading={isLoading}
                  loadingText={isCreatingMultiple ? "Creating schedules..." : "Creating..."}
                  flex={1}
                >
                  {isCreatingMultiple ? 'Create Timetables' : 'Create Timetable'}
                </Button>
              </HStack>
            </VStack>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default function TimetablesPage() {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedDay, setSelectedDay] = useState('all')
  const [selectedDaladala, setSelectedDaladala] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const toast = useToast()
  const queryClient = useQueryClient()

  // Fetch real data from API
  const { data: timetablesData, isLoading: timetablesLoading } = useQuery(
    'timetables',
    () => cmsService.getTimetables({ limit: 100 }),
    {
      retry: false,
    }
  )

  const { data: daladalasData } = useQuery('vehicles', () => cmsService.getVehicles({ limit: 100 }))

  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, selectedDay, selectedDaladala])

  // Use real data only - no fallbacks
  const timetables: Timetable[] = timetablesData?.data || []
  const daladalas = daladalasData?.data || []
  
  const dayNames = [
    'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
  ]

  const stats = [
    { title: "Total Schedules", value: timetables.length, color: "blue", icon: Calendar },
    { title: "Active Schedules", value: timetables.filter(t => t.active).length, color: "green", icon: CheckCircle },
    { title: "Inactive Schedules", value: timetables.filter(t => !t.active).length, color: "red", icon: XCircle },
    { title: "Daladalas Scheduled", value: new Set(timetables.map(t => t.daladalaId)).size, color: "purple", icon: Truck },
  ]

  const filteredTimetables = timetables.filter((timetable: Timetable) => {
    const daladalaNumber = timetable.daladala?.registrationNumber.toLowerCase() || ''
    const routeName = timetable.route?.name.toLowerCase() || ''
    const driverName = timetable.daladala?.driver ? 
      `${timetable.daladala.driver.firstName} ${timetable.daladala.driver.lastName}`.toLowerCase() : ''
    
    const matchesSearch = daladalaNumber.includes(searchTerm.toLowerCase()) ||
                         routeName.includes(searchTerm.toLowerCase()) ||
                         driverName.includes(searchTerm.toLowerCase())
    
    const matchesDay = selectedDay === 'all' || timetable.dayOfWeek === parseInt(selectedDay)
    const matchesDaladala = selectedDaladala === 'all' || timetable.daladalaId === selectedDaladala
    
    return matchesSearch && matchesDay && matchesDaladala
  })

  // Pagination logic
  const totalPages = Math.ceil(filteredTimetables.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentTimetables = filteredTimetables.slice(startIndex, endIndex)

  const handleStatusToggle = async (timetableId: string, currentStatus: boolean) => {
    try {
      await cmsService.updateTimetable(timetableId, { active: !currentStatus })
      await queryClient.invalidateQueries('timetables')
      toast({
        title: 'Status Updated',
        description: 'Timetable status has been updated successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
    } catch (error: any) {
      toast({
        title: 'Error',
        description: getErrorMessage(error, 'Failed to update timetable status.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  const handleDelete = async (timetableId: string) => {
    try {
      await cmsService.deleteTimetable(timetableId)
      await queryClient.invalidateQueries('timetables')
      toast({
        title: 'Timetable Deleted',
        description: 'Timetable has been successfully deleted.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
    } catch (error: any) {
      toast({
        title: 'Error',
        description: getErrorMessage(error, 'Failed to delete timetable.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  // Show loading state
  if (timetablesLoading) {
    return (
      <Center h="64">
        <Spinner size="lg" color="brand.500" />
      </Center>
    )
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <Flex align="center" justify="space-between" flexDirection={{ base: 'column', md: 'row' }} gap={4}>
        <VStack align={{ base: 'center', md: 'start' }} spacing={2}>
          <Heading size="xl" color="gray.900">
            Timetable Management
          </Heading>
          <Text color="gray.600">
            Plan and manage daily schedules for daladala routes
          </Text>
        </VStack>
        <HStack spacing={3}>
          <Button
            leftIcon={<Download size={16} />}
            variant="outline"
            size="sm"
          >
            Export
          </Button>
          <Button
            leftIcon={<Plus size={16} />}
            colorScheme="brand"
            onClick={onOpen}
          >
            Create Timetable
          </Button>
        </HStack>
      </Flex>

      {/* Stats Cards */}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
        {stats.map((stat, index) => (
          <Card key={index} bg="white" shadow="sm">
            <CardBody>
              <HStack justify="space-between">
                <VStack align="start" spacing={1}>
                  <Text fontSize="sm" color="gray.600" fontWeight="medium">
                    {stat.title}
                  </Text>
                  <Text fontSize="xl" fontWeight="bold" color="gray.900">
                    {stat.value}
                  </Text>
                </VStack>
                <Box
                  p={2}
                  borderRadius="lg"
                  bg={`${stat.color}.100`}
                  color={`${stat.color}.600`}
                >
                  <stat.icon size={20} />
                </Box>
              </HStack>
            </CardBody>
          </Card>
        ))}
      </SimpleGrid>

      {/* Filters and Search */}
      <Card bg="white" shadow="sm">
        <CardBody>
          <HStack spacing={4} flexDirection={{ base: 'column', md: 'row' }}>
            <InputGroup maxW="400px">
              <InputLeftElement pointerEvents="none">
                <Search size={16} color="var(--chakra-colors-gray-400)" />
              </InputLeftElement>
              <Input
                placeholder="Search timetables by daladala, route, or driver..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
            <Select
              maxW="200px"
              value={selectedDay}
              onChange={(e) => setSelectedDay(e.target.value)}
            >
              <option value="all">All Days</option>
              {dayNames.map((day, index) => (
                <option key={index} value={index}>
                  {day}
                </option>
              ))}
            </Select>
            <Select
              maxW="200px"
              value={selectedDaladala}
              onChange={(e) => setSelectedDaladala(e.target.value)}
            >
              <option value="all">All Daladalas</option>
              {daladalas.map((daladala: any) => (
                <option key={daladala.id} value={daladala.id}>
                  {daladala.registrationNumber}
                </option>
              ))}
            </Select>
          </HStack>
        </CardBody>
      </Card>

      {/* Timetables Table */}
      <Card bg="white" shadow="sm">
        <CardHeader>
          <Flex justify="space-between" align="center">
            <Heading size="md" color="gray.900">
              Timetables List
            </Heading>
            <Text color="gray.600" fontSize="sm">
              {filteredTimetables.length} schedule{filteredTimetables.length !== 1 ? 's' : ''}
            </Text>
          </Flex>
        </CardHeader>
        <CardBody>
          <Box overflowX="auto">
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Day</Th>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Daladala</Th>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Route</Th>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Driver</Th>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Schedule</Th>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Duration</Th>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Status</Th>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredTimetables.length === 0 ? (
                  <Tr>
                    <Td colSpan={8} py={12}>
                      <Center>
                        <VStack spacing={4}>
                          <Box p={4} bg="gray.100" borderRadius="full">
                            <Calendar size={48} color="var(--chakra-colors-gray-500)" />
                          </Box>
                          <VStack spacing={2}>
                            <Heading size="md" color="gray.900">
                              No timetables found
                            </Heading>
                            <Text color="gray.600" textAlign="center">
                              {searchTerm || selectedDay !== 'all' || selectedDaladala !== 'all'
                                ? 'No timetables match your current filters.'
                                : 'Get started by creating your first timetable.'
                              }
                            </Text>
                          </VStack>
                          {!searchTerm && selectedDay === 'all' && selectedDaladala === 'all' && (
                            <Button 
                              colorScheme="brand" 
                              leftIcon={<Plus size={16} />}
                              onClick={onOpen}
                              borderRadius="xl"
                            >
                              Create Your First Timetable
                            </Button>
                          )}
                        </VStack>
                      </Center>
                    </Td>
                  </Tr>
                ) : (
                  currentTimetables.map((timetable: Timetable) => {
                    const departureLabel = formatTime(timetable.departureTime)
                    const arrivalLabel = formatTime(timetable.arrivalTime)
                    const durationLabel = formatDuration(timetable.departureTime, timetable.arrivalTime)

                    return (
                      <Tr key={timetable.id} _hover={{ bg: 'gray.50' }} borderBottom="1px solid" borderColor="gray.100">
                        <Td py={4}>
                          <Badge
                            colorScheme="blue"
                            variant="subtle"
                            px={3}
                            py={1}
                            borderRadius="full"
                          >
                            {dayNames[timetable.dayOfWeek]}
                          </Badge>
                        </Td>
                        <Td py={4}>
                          <HStack spacing={2}>
                            <Truck size={14} color="var(--chakra-colors-blue-500)" />
                            <Text fontSize="sm" color="gray.900" fontWeight="medium">
                              {timetable.daladala?.registrationNumber || 'N/A'}
                            </Text>
                          </HStack>
                        </Td>
                        <Td py={4}>
                          <VStack align="start" spacing={1}>
                            <Text fontSize="sm" color="gray.900" fontWeight="medium">
                              {timetable.route?.name || 'N/A'}
                            </Text>
                            <Text fontSize="xs" color="gray.600">
                              {timetable.route?.startPoint || 'N/A'} → {timetable.route?.endPoint || 'N/A'}
                            </Text>
                          </VStack>
                        </Td>
                        <Td py={4}>
                          {timetable.daladala?.driver ? (
                            <VStack align="start" spacing={0}>
                              <Text fontSize="sm" color="gray.900">
                                {[timetable.daladala.driver.firstName, timetable.daladala.driver.lastName].filter(Boolean).join(' ') || 'Driver'}
                              </Text>
                              {timetable.daladala.driver.phoneNumber && (
                                <Text fontSize="xs" color="gray.600">
                                  {timetable.daladala.driver.phoneNumber}
                                </Text>
                              )}
                            </VStack>
                          ) : (
                            <Text fontSize="sm" color="gray.500">No Driver</Text>
                          )}
                        </Td>
                        <Td py={4}>
                          <VStack align="start" spacing={1}>
                            <HStack spacing={2}>
                              <Clock size={12} color="var(--chakra-colors-blue-500)" />
                              <Text fontSize="sm" color="gray.900">
                                {departureLabel}
                              </Text>
                            </HStack>
                            <HStack spacing={2}>
                              <Navigation size={12} color="var(--chakra-colors-green-500)" />
                              <Text fontSize="sm" color="gray.900">
                                {arrivalLabel}
                              </Text>
                            </HStack>
                          </VStack>
                        </Td>
                        <Td py={4}>
                          <Text fontSize="sm" color="gray.600">
                            {durationLabel}
                          </Text>
                        </Td>
                        <Td py={4}>
                          <Badge
                            colorScheme={timetable.active ? 'green' : 'red'}
                            variant="subtle"
                            px={3}
                            py={1}
                            borderRadius="full"
                          >
                            {timetable.active ? 'Active' : 'Inactive'}
                          </Badge>
                        </Td>
                        <Td py={4}>
                          <HStack spacing={1}>
                            <Tooltip label="View Details">
                              <IconButton
                                aria-label="View"
                                icon={<Eye size={16} />}
                                size="sm"
                                variant="ghost"
                                colorScheme="blue"
                              />
                            </Tooltip>
                            <Tooltip label="Edit Timetable">
                              <IconButton
                                aria-label="Edit"
                                icon={<Edit size={16} />}
                                size="sm"
                                variant="ghost"
                                colorScheme="blue"
                              />
                            </Tooltip>
                            <Tooltip label={timetable.active ? 'Deactivate' : 'Activate'}>
                              <Switch
                                size="sm"
                                isChecked={timetable.active}
                                onChange={() => handleStatusToggle(timetable.id, timetable.active)}
                                colorScheme="green"
                              />
                            </Tooltip>
                            <Tooltip label="Delete Timetable">
                              <IconButton
                                aria-label="Delete"
                                icon={<Trash2 size={16} />}
                                size="sm"
                                variant="ghost"
                                colorScheme="red"
                                onClick={() => handleDelete(timetable.id)}
                              />
                            </Tooltip>
                          </HStack>
                        </Td>
                      </Tr>
                    )
                  })
                )}
              </Tbody>
            </Table>
          </Box>
        </CardBody>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <HStack justify="center" py={4} borderTop="1px solid" borderColor="gray.100">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              isDisabled={currentPage === 1}
            >
              Previous
            </Button>
            <Text fontSize="sm" color="gray.600" px={4}>
              Page {currentPage} of {totalPages}
            </Text>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              isDisabled={currentPage === totalPages}
            >
              Next
            </Button>
          </HStack>
        )}
      </Card>

      {/* Create Timetable Modal */}
      <TimetableFormModal isOpen={isOpen} onClose={onClose} />
    </VStack>
  )
}
